/*
 * <oilogic-marquee> — seamless infinite horizontal marquee.
 *
 * Progressive enhancement over a CSS keyframe marquee that translates the track
 * 0 → -50%. That loop is only seamless when one copy of the content is wider than
 * the container; with few items on a wide viewport the wrap exposes empty space and
 * snaps. This element clones the canonical set as many times as needed so the track
 * is always at least twice the container width (an even number of identical sets),
 * which keeps the -50% wrap seamless for any item count / viewport. Scroll speed is
 * normalised so it stays constant regardless of how many clones are added.
 *
 * Expected markup (the host is the overflow viewport):
 *   <oilogic-marquee data-speed="30">
 *     <div data-marquee-track>
 *       <ul> … </ul>   <!-- canonical set (first child); extra server copies optional -->
 *     </div>
 *   </oilogic-marquee>
 *
 * Wrapped in an IIFE: these files load as classic <script defer> tags, whose
 * top-level const/class declarations share one global lexical scope. The IIFE
 * keeps REF_WIDTH et al. local so they can't collide with another component's
 * identically named constants (e.g. <oilogic-curve-marquee>'s own REF_WIDTH).
 */
(() => {
  // footer + retailers both load this script, so a page with both would run it
  // twice and re-define the element. Define at most once per page.
  if (customElements.get('oilogic-marquee')) return;

  const REF_WIDTH = 1920; // speed reference: content crosses this width in `speed` seconds
  const MAX_SETS = 60; // safety cap so a tiny base set can't clone unbounded

  class OilogicMarquee extends HTMLElement {
    #ctrl = new AbortController();
    #raf = 0;

    connectedCallback() {
      this.track = this.querySelector('[data-marquee-track]');
      this.baseSet = this.track?.querySelector(':scope > *');
      if (!this.track || !this.baseSet) return;

      const rebuild = () => this.#schedule();
      window.addEventListener('resize', rebuild, { signal: this.#ctrl.signal });
      document.addEventListener('shopify:section:load', rebuild, { signal: this.#ctrl.signal });

      // Logos are images with auto width — measure only once they have dimensions.
      this.#whenReady().then(() => {
        if (this.isConnected) this.#build();
      });
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.#raf);
      this.#ctrl.abort();
    }

    #whenReady() {
      const pending = Array.from(this.baseSet.querySelectorAll('img')).filter((img) => !img.complete);
      if (!pending.length) return Promise.resolve();
      return Promise.all(
        pending.map(
          (img) =>
            new Promise((resolve) => {
              img.addEventListener('load', resolve, { once: true });
              img.addEventListener('error', resolve, { once: true });
            })
        )
      );
    }

    #schedule() {
      cancelAnimationFrame(this.#raf);
      this.#raf = requestAnimationFrame(() => this.#build());
    }

    #build() {
      // Reduced motion: leave the server-rendered copies for the CSS static fallback.
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

      // Reset to the canonical set so rebuilds (resize / editor) stay idempotent.
      for (const child of Array.from(this.track.children)) {
        if (child !== this.baseSet) child.remove();
      }

      const containerWidth = this.getBoundingClientRect().width;
      const baseSetWidth = this.baseSet.getBoundingClientRect().width;
      if (!containerWidth || !baseSetWidth) return;

      // Each half of the track must be at least as wide as the container, so the
      // -50% wrap always lands on identical content with no gap.
      const copiesPerHalf = Math.min(MAX_SETS / 2, Math.max(1, Math.ceil(containerWidth / baseSetWidth)));
      const totalSets = copiesPerHalf * 2;

      for (let i = 1; i < totalSets; i++) {
        const clone = this.baseSet.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-marquee-clone', '');
        clone.querySelectorAll('a').forEach((link) => link.setAttribute('tabindex', '-1'));
        this.track.append(clone);
      }

      // Keep px/second constant: one -50% cycle travels half the track (copiesPerHalf
      // base sets). `speed` = seconds to cross REF_WIDTH, matching <oilogic-curve-marquee>.
      const speed = Math.max(5, parseInt(this.dataset.speed, 10) || 30);
      const halfWidth = copiesPerHalf * baseSetWidth;
      this.track.style.animationDuration = `${(halfWidth / REF_WIDTH) * speed}s`;
    }
  }

  customElements.define('oilogic-marquee', OilogicMarquee);
})();
